import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import type { SalesforceAccountOrg } from '@/generated/http-clients/v3ApiSchemas'
import { Table, Text, VStack } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

type Props = {
  orgs: SalesforceAccountOrg[]
}

/**
 * The organisations linked to one Salesforce account.
 *
 * Every row is a link to that organisation. The link is a full-row overlay
 * anchor, the same pattern as `LicensesCard`: the row is the positioned
 * ancestor, the anchor spans it with `inset: 0`, and the cell content sits
 * above it at `zIndex 1` so text stays selectable and any future control in a
 * cell keeps its own click.
 *
 * `orgName` is optional in the contract and an unnamed organisation is never
 * dropped from the list — it is precisely the row an operator is hunting for.
 * A missing name renders a placeholder in the name column and the row keeps a
 * working link, with the id carrying the anchor's accessible name.
 */
export function AccountOrgsTable({ orgs }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <VStack align="stretch" gap={2}>
      <Table.Root
        size="sm"
        aria-label={String(t('salesforceAccounts.detail.orgs.tableLabel'))}
      >
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              {String(t('salesforceAccounts.detail.orgs.columns.orgId'))}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {String(t('salesforceAccounts.detail.orgs.columns.orgName'))}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {String(t('salesforceAccounts.detail.orgs.columns.ownerId'))}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {orgs.map((org) => {
            // Trimmed at the read surface: the contract promises a name is
            // absent when unknown, not that a present one is tidy, and a
            // whitespace-only name would render as a blank cell that reads as
            // a bug rather than as "no name".
            const displayName = org.orgName?.trim()
            return (
              <Table.Row
                key={org.orgId}
                position="relative"
                cursor="pointer"
                _hover={{ bg: 'bg.muted' }}
              >
                <Table.Cell>
                  <Link
                    to={hivesOverviewOrganizationDetailsRoute.to}
                    params={{ orgId: org.orgId }}
                    style={{ position: 'absolute', inset: 0, zIndex: 0 }}
                    aria-label={String(
                      t('salesforceAccounts.detail.orgs.openOrg', {
                        // The id, not the name: it is the value that always
                        // exists.
                        orgId: org.orgId,
                      }),
                    )}
                  />
                  {/*
                    Deliberately NOT wrapped in a positioned, zIndex-raised
                    Box. `LicensesCard` raises its first cell because that
                    cell holds a DataTag with its own copy button, which has
                    to receive clicks instead of the overlay anchor. Nothing
                    here is interactive, so raising it would lift the text
                    above the anchor and swallow the click on the id.
                  */}
                  <Text fontFamily="mono" fontSize="xs">
                    {org.orgId}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  {displayName ? (
                    <Text>{displayName}</Text>
                  ) : (
                    <Text color="text.text-subtle">
                      {String(t('salesforceAccounts.detail.orgs.unnamedOrg'))}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Text fontFamily="mono" fontSize="xs">
                    {org.ownerId}
                  </Text>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>

      {/* The contract's ordering is deterministic but means nothing — saying
          so stops "first row" being read as "the primary organisation". */}
      <Text fontSize="xs" color="text.text-subtle">
        {String(t('salesforceAccounts.detail.orgs.orderNote'))}
      </Text>
    </VStack>
  )
}
